import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { GIT_COMMIT } from "./common/envconst";
import { useLocalStorage } from "./common/hooks/useLocalStorage";
import { useSystemInfo } from "./common/hooks/useSystemInfo";
import { useUserAgent } from "./common/hooks/useUserAgent";
import Button from "./components/elements/Button/Button";
import ViewPort from "./components/elements/ViewPort/ViewPort";
import { useConfigActions } from "./contexts/ConfigContext/ConfigContext";
import { IStallMode } from "./contexts/ConfigContext/types";

const Info = () => {
	const navigate = useNavigate();
	const userAgent = useUserAgent();
	const configAction = useConfigActions();
	const systemInfo = useSystemInfo();
	const { storedValue: currentTheme, setValue: setCurrentTheme } = useLocalStorage<
		string | undefined
	>("info-page-theme", undefined);

	const handleGoBack = useCallback(() => {
		navigate(-1);
	}, [navigate]);

	const handleThemeToggle = useCallback(() => {
		const newTheme = (() => {
			switch (currentTheme) {
				case "wang":
					return IStallMode.FAIRPRICE;
				case "pdd":
					return IStallMode.WANG;
				case "fling":
					return IStallMode.FLINT;
				default:
					return IStallMode.WANG;
			}
		})();

		configAction.SET_STALL_MODE(newTheme);
		setCurrentTheme(newTheme);
	}, [currentTheme, setCurrentTheme]);

	const getThemeDisplayName = useCallback((theme: string | undefined) => {
		switch (theme) {
			case IStallMode.WANG:
				return "Wang";
			case IStallMode.FAIRPRICE:
				return "PDD";
			case IStallMode.FLINT:
				return "Flint";
			default:
				return "Default";
		}
	}, []);

	return (
		<ViewPort
			container
			className="InfoPage"
			direction="column"
			spacing={2}
			data-testid="InfoPageCntr"
		>
			<ViewPort size="auto" data-testid="InfoPageTitleCntr">
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<h1>System Information</h1>
					<Button
						onClick={handleThemeToggle}
						className="ThemeToggleBtn"
						data-testid="ThemeToggleBtn"
						style={{
							padding: "8px 16px",
							fontSize: "14px",
							minWidth: "80px",
						}}
					>
						{getThemeDisplayName(currentTheme)}
					</Button>
				</div>
			</ViewPort>

			<ViewPort size="auto" data-testid="InfoPageDescCntr">
				<p>Detailed system information for debugging and troubleshooting</p>
			</ViewPort>

			<ViewPort size="auto" data-testid="InfoPageProgressCntr">
				<div className="InfoPageProgress">
					<p>Version: {GIT_COMMIT?.slice(0, 7)}</p>
					<p>
						Browser: {userAgent.browser.name} {userAgent.browser.version}
					</p>
					<p>OS: {userAgent.os.name}</p>
					<p>Device: {userAgent.device.type}</p>
					<p>
						Screen: {systemInfo.screen.width}×{systemInfo.screen.height}
					</p>
					<p>
						Network: {systemInfo.network.online ? "Online" : "Offline"} (
						{systemInfo.network.effectiveType || "Unknown"})
					</p>
					<p>
						Memory:{" "}
						{systemInfo.memory.usedJSHeapSize
							? `${(systemInfo.memory.usedJSHeapSize / 1024 / 1024).toFixed(0)}MB`
							: "N/A"}
					</p>
					<p>Session ID: {systemInfo.datadog.rum.session_id || "N/A"}</p>
					<p>Trace ID: {systemInfo.datadog.traceId || "N/A"}</p>
					<p>Cookies: {systemInfo.cookies.count} items</p>
					{systemInfo.cookies.details.map((cookie) => (
						<p key={cookie.name} style={{ marginLeft: "20px" }}>
							• {cookie.name}: {cookie.value}...
						</p>
					))}
					<p>Timezone: {systemInfo.timezone}</p>
				</div>
			</ViewPort>

			<ViewPort container spacing={2} data-testid="InfoPageActionCntr">
				<ViewPort data-testid="InfoPageBackBtnCntr">
					<Button onClick={handleGoBack} className="InfoPageBackBtn" data-testid="InfoPageBackBtn">
						Go Back
					</Button>
				</ViewPort>
			</ViewPort>
		</ViewPort>
	);
};

export default React.memo(Info);
